/*
 * @author Andrey Patyuchenko
 * @email  apatyuchenko@atlassian.com
 * @email  andrey.patyuchenko@gmail.com
 * @date   3/10/22
 */

import React from "react";
import {
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	FormHelperText,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	TextField
} from "@mui/material";
import EmployeesControl from "../controls/EmployeesControl";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import DateRangePicker from "../components/DateRangePicker";
import { addDays, addHours, addMinutes, differenceInHours, startOfDay, startOfToday, startOfTomorrow } from "date-fns";
import {
	CalendarEventInput,
	CalendarEventVars,
	CreateCalendarEventMutation,
	CreateCalendarEventResponse,
	UpdateCalendarEventMutation,
	UpdateCalendarEventResponse,
	UpdateCalendarEventVars
} from "../graphql/mutations/calendar-events";
import zonedTimeToUtc from "date-fns-tz/esm/zonedTimeToUtc";
import { useMutation, useQuery } from "urql";
import { EmployeeQuery, EmployeeQueryResponse, EmployeeQueryVars } from "../graphql/queries/employees";
import { LoadingButton } from "@mui/lab";
import { Employee, VegaEvent } from "../interfaces/Global";
import { useSelector } from "react-redux";
import { State } from "../state/store";
import DateTimeRangePicker from "../components/DateTimeRangePicker";
import { getEventType } from "./event-types";
import { utcToZonedTime } from "date-fns-tz";
import CapacityModifierControl from "../controls/CapacityModifierControl";
import EventTypeSelector from "../controls/EventTypeSelector";
import { toast } from "react-toastify";

export interface EventDialogProps {
	op: "create" | "update";
	open: boolean;
	onClose: () => void;
	model: Partial<VegaEvent>;
	employee?: Employee;
}

const getDayPartInterval = (part: "am" | "pm", day: Date, startTime: string, endTime: string): Interval => {
	const [ sH, sM ] = startTime.split(":").map(i => Number(i));
	const [ eH, eM ] = endTime.split(":").map(i => Number(i));
	return part === "am" ? {
		start: addMinutes(addHours(startOfDay(day), sH), sM),
		end: addMinutes(addHours(startOfDay(day), sH + 4), sM)
	} : {
		start: addMinutes(addHours(startOfDay(day), eH - 4), eM),
		end: addMinutes(addHours(startOfDay(day), eH), sM)
	};
};

export default function EventDialog({ op, model, open, onClose, employee }: EventDialogProps) {

	const user = useSelector((s: State) => s.user);
	const recentEventTypes = useSelector((s:State) => s.recentEventTypes)
	const monthEventMinDurationHours = useSelector((s:State) => s.settings.monthEventMinDurationHours);

	const [ employeeId, setEmployeeId ] = React.useState(model.employeeId);
	const [ typeId, setTypeId ] = React.useState(model.typeId ?? recentEventTypes[0] ?? "AL");
	const eventType = React.useMemo(() => getEventType(typeId), [ typeId ]);
	const [ startDatetime, setStartDatetime ] = React.useState(() => {
		return model.startDatetime ? utcToZonedTime(model.startDatetime, employee?.timeZone || "UTC") : startOfToday();
	});
	const [ endDatetime, setEndDatetime ] = React.useState(() => {
		return model.endDatetime ? utcToZonedTime(model.endDatetime, employee?.timeZone || "UTC") : startOfTomorrow();
	});
	const [ rangeType, setRangeType ] = React.useState(() => {
		const diffHours = differenceInHours(endDatetime, startDatetime);
		if (diffHours > 24 && diffHours % 24 === 0) {
			return "range";
		} else if (diffHours === 4) {
			return "half-day";
		} else if (diffHours !== 24) {
			return "custom";
		}
		return "single-day";
	});
	const [ dayPart, setDayPart ] = React.useState<"am" | "pm">(() => {
		if (employee && rangeType === "half-day") {
			const [ employeeEndHour, employeeEndMin ] = employee.endTime.split(":").map(i => Number(i));
			const employeeEnd = zonedTimeToUtc(addMinutes(addHours(startOfDay(endDatetime), employeeEndHour), employeeEndMin), employee.timeZone);
			const zonedEndDatetime = zonedTimeToUtc(endDatetime, employee.timeZone);
			if (employeeEnd.getHours() === zonedEndDatetime.getHours() && employeeEnd.getMinutes() === zonedEndDatetime.getMinutes()) {
				return "pm";
			}
		}
		return "am";
	});
	const [ isConfirmed, setIsConfirmed ] = React.useState(model.isConfirmed ?? false);
	const [ description, setDescription ] = React.useState(model.description ?? "");
	const [ capacityModifier, setCapacityModifier ] = React.useState(() => {
		return model.capacityModifier ?? (eventType?.isUnavailable ? 0 : employee?.capacityModifier ?? 1);
	});
	const [ errors, setErrors ] = React.useState<Record<string, string>>({});

	const [ createResult, createCalendarEvent ] = useMutation<CreateCalendarEventResponse, CalendarEventVars>(CreateCalendarEventMutation);
	const [ updateResult, updateCalendarEvent ] = useMutation<UpdateCalendarEventResponse, UpdateCalendarEventVars>(UpdateCalendarEventMutation);

	const [ employeeResult ] = useQuery<EmployeeQueryResponse, EmployeeQueryVars>({
		query: EmployeeQuery,
		variables: { id: employeeId as string },
		pause: !employeeId
	});

	const [ employeeStartTime, employeeEndTime, firstInterval, secondInterval, employeeTz ] = React.useMemo(() => {
		const employeeTz = employeeResult.data?.employee.timeZone || "UTC";
		const employeeStartTime = employeeResult.data?.employee.startTime || "9:00";
		const employeeEndTime = employeeResult.data?.employee.endTime || "18:00";
		const [ sH, sM ] = employeeStartTime.split(":").map(i => Number(i));
		const [ eH, eM ] = employeeEndTime.split(":").map(i => Number(i));
		const firstInterval = sH + ":" + sM + " - " + (sH + 4) + ":" + sM;
		const secondInterval = (eH - 4) + ":" + eM + " - " + eH + ":" + eM;
		return [ employeeStartTime, employeeEndTime, firstInterval, secondInterval, employeeTz ];
	}, [ employeeResult ]);

	const onSubmit = () => {
		if (employeeId) {
			setErrors({});
			let from = startDatetime, to = endDatetime;
			if (rangeType === "half-day") {
				const range = getDayPartInterval(dayPart, startDatetime, employeeStartTime, employeeEndTime);
				from = range.start as Date;
				to = range.end as Date;
			} else if (rangeType !== "custom") {
				from = startOfDay(startDatetime);
				to = startOfDay(endDatetime);
			}
			const start = zonedTimeToUtc(from, employeeTz), end = zonedTimeToUtc(to, employeeTz);
			const data: CalendarEventInput = {
				employeeId,
				typeId,
				startDatetime: start,
				endDatetime: end,
				isConfirmed,
				description,
				capacityModifier
			};
			const promise = op === "create" ? createCalendarEvent({ data }) : updateCalendarEvent({
				id: model.id as number,
				data
			});
			onClose();
			promise.then(response => {
				if (response.error) {
					const serverErrors = ((response.error.graphQLErrors[0].extensions as Record<string, any>) || {}).data as Record<string, string>;
					console.error(serverErrors);
					toast.error(Object.entries(serverErrors).map(([ attribute, error ]) => `${attribute}: ${error}`).join(", "), {
						autoClose: 10_000
					});
				} else if (differenceInHours(end, start) < monthEventMinDurationHours) {
					toast.warning("The event was saved but hidden from the Month view due to your display settings.\nCheck \"Minimum events duration in Month Calendar\" option in display settings of your calendar.", {
						autoClose: false,
					});
				}
			});
		} else {
			setErrors(errors => ({ ...errors, employeeId: "This value is required" }));
		}
	};

	React.useEffect(() => {
		if (rangeType === "single-day") {
			setEndDatetime(addDays(startOfDay(startDatetime), 1));
		}
	}, [ rangeType, startDatetime ]);

	React.useEffect(() => {
		setCapacityModifier(model.capacityModifier ?? (eventType?.isUnavailable ? 0 : employee?.capacityModifier ?? 1));
	}, [ employee?.capacityModifier, eventType, model.capacityModifier ]);

	return (
		<Dialog
			open={open}
			onClose={onClose}
			fullWidth
			maxWidth={"xs"}
		>
			<DialogTitle>{op === "create" ? "New Event" : "Edit Event"}</DialogTitle>
			<DialogContent>
				<Stack spacing={2}>
					{!model.id &&
						<FormControl variant={"standard"} fullWidth error={"employeeId" in errors}>
							<EmployeesControl
								label={"Employee"}
								values={employeeId ? [ employeeId ] : []}
								defaultNames={employee ? { [employee.id]: employee.displayName } : {}}
								onChange={(values) => {
									if (values.length > 0) {
										setEmployeeId(values[0]);
									} else {
										setEmployeeId(undefined);
									}
								}}
							/>
							{"employeeId" in errors && <FormHelperText>{errors["employeeId"]}</FormHelperText>}
						</FormControl>}
					<FormControl variant={"standard"} fullWidth error={"typeId" in errors}>
						<EventTypeSelector
							value={typeId}
							onChange={setTypeId}
						/>
						{"typeId" in errors && <FormHelperText>{errors["typeId"]}</FormHelperText>}
					</FormControl>
					<FormControl variant={"standard"} fullWidth>
						<InputLabel>Range Type</InputLabel>
						<Select
							value={rangeType}
							onChange={event => {
								setRangeType(event.target.value as string);
							}}
						>
							<MenuItem value={"range"}>Date Range</MenuItem>
							<MenuItem value={"single-day"}>Single Day</MenuItem>
							<MenuItem value={"half-day"}>Half Day</MenuItem>
							<MenuItem value={"custom"}>Custom</MenuItem>
						</Select>
					</FormControl>
					<FormControl variant={"standard"} fullWidth error={"startDatetime" in errors}>
						{rangeType === "single-day" &&
							<DatePicker
								label={"Day"}
								value={startDatetime}
								onChange={(value: any) => {
									if (value) {
										setStartDatetime(value);
									}
								}}
								renderInput={(params: any) =>
									<TextField
										sx={{ width: "140px" }}
										variant={"standard"}
										{...params}
									/>
								}
							/>}
						{rangeType === "range" &&
							<DateRangePicker
								startDateLabel={"First Day"}
								endDateLabel={"Last Day"}
								range={{
									start: startDatetime,
									end: addDays(endDatetime, -1)
								}}
								onChange={(range) => {
									setStartDatetime(range.start as Date);
									setEndDatetime(addDays(range.end as Date, 1));
								}}
							/>}
						{rangeType === "half-day" &&
							<Stack direction={"row"} spacing={2}>
								<DatePicker
									label={"Day"}
									value={startDatetime}
									onChange={(value: any) => {
										if (value) {
											setStartDatetime(value);
										}
									}}
									renderInput={(params: any) =>
										<TextField
											sx={{ width: "140px" }}
											variant={"standard"}
											{...params}
										/>
									}
								/>
								<FormControl variant={"standard"} error={"startDatetime" in errors}>
									<InputLabel>Day Part</InputLabel>
									<Select
										value={dayPart}
										onChange={event => {
											setDayPart(event.target.value as "am" | "pm");
										}}
									>
										<MenuItem value={"am"}>First Half ({firstInterval})</MenuItem>
										<MenuItem value={"pm"}>Second Half ({secondInterval})</MenuItem>
									</Select>
									{"startDatetime" in errors &&
										<FormHelperText>{errors["startDatetime"]}</FormHelperText>}
								</FormControl>
							</Stack>}
						{rangeType === "custom" &&
							<DateTimeRangePicker
								range={{
									start: startDatetime,
									end: endDatetime
								}}
								onChange={(range) => {
									setStartDatetime(range.start as Date);
									setEndDatetime(range.end as Date);
								}}
							/>}
						{("startDatetime" in errors || (user?.id !== employeeId && rangeType === "custom")) &&
							<FormHelperText>
								{errors["startDatetime"] ?? "Time should be specified as local to an employee selected."}
							</FormHelperText>}
					</FormControl>
					<CapacityModifierControl
						disabled={!!eventType?.parentId && ![ "EVT", "A", "ACT" ].includes(eventType.parentId)}
						value={capacityModifier}
						setValue={setCapacityModifier}
					/>
					<FormControl variant={"standard"} fullWidth>
						<FormControlLabel
							control={
								<Checkbox
									checked={isConfirmed}
									onChange={(event) => {
										setIsConfirmed(event.target.checked);
									}}
								/>
							}
							label="Confirmed"
						/>
					</FormControl>
					<FormControl variant={"standard"} fullWidth>
						<TextField
							label="Description"
							placeholder={"Optional"}
							multiline
							rows={5}
							value={description}
							onChange={(event) => {
								setDescription(event.target.value);
							}}
						/>
					</FormControl>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Cancel</Button>
				<LoadingButton
					loading={createResult.fetching || updateResult.fetching}
					disabled={!employeeId}
					onClick={onSubmit}
					variant={"contained"}
					color={"primary"}
				>
					{op === "create" ? "Create" : "Update"}
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);

}
